import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Button, Container } from "reactstrap";

import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Admin/AdminConfiguration/AdminConfiguration.module.css";
import DefaultColumnFilter from "components/ReactTable/Components/DefaultColumnFilter/DefaultColumnFilter";
import ManageUsersModal from "./ManageUsersModal/ManageUsersModal";
import ResetPasswordModal from "./ManageUsersModal/ResetPasswordModal";
import usePageTitle from "Hooks/usePageTitle";

export type ActionType =
    | "newProcore"
    | "newDimension"
    | "edit"
    | "delete"
    | "password"
    | "";
export interface ICompanyUserWithUser {
    company_id: string;
    user_id: string;
    activation_code: string;
    created_date: Date;
    modified_date: Date;
    email: string;
    is_calance_admin: boolean;
    enabled: boolean;
    name: string;
    password: string;
    permissions: string;
    login_type: string | null;
}
export interface IRole {
    company_id: string;
    name: string;
    permissions: string;
    created_date: Date;
    modified_date: Date;
}

interface pageData {
    users: ICompanyUserWithUser[];
    roles: IRole[];
    integrations: IIntegration[];
}

export default function ManageUsers() {
    const [pageData, setPageData] = useState<pageData>({
        users: [],
        roles: [],
        integrations: [],
    });
    const [activeModal, setActiveModal] = useState<ActionType>("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(undefined);

    usePageTitle("Manage Users");
    const { get, post } = useFetch();

    const ignore = useRef(false);

    const getPageData = useCallback(async () => {
        if (ignore.current) return;

        setIsLoading(true);

        const response = await get(`api/users/manage`);

        let data: Response | pageData;
        try {
            data = await response.json();
        } catch (e) {
            data = response;
        }
        console.log("Get user response: ", data);

        if (response.ok && !ignore.current) {
            data = data as pageData;
            setPageData(data);
        }
        setIsLoading(false);
    }, [get]);

    // Get users on page load
    useEffect(() => {
        ignore.current = false;

        getPageData();

        return () => {
            ignore.current = true;
        };
    }, [get, getPageData, post]);

    /** Wraps state setting callback to pass to child component and update state from parent */
    const onSetActiveModal = useCallback((modal: ActionType) => {
        setActiveModal(modal);
    }, []);

    /** Wraps state setting callback to pass to child component and update state from parent */
    function onSetSelectedRow(row: any) {
        setSelectedRow(row);
    }

    /** Passed down to table as rowDoubleClickCallback */
    const editRow = useCallback((row: any) => {
        setActiveModal("edit");
        setSelectedRow(row);
    }, []);

    const columns = useMemo(() => {
        const cellContent = (value: string) => {
            return <span className={styles.textBox}>{value}</span>;
        };

        const sourceLabels: Record<string, string> = {
            dimension: "Dimension",
            procore: "Procore",
            unknown: "Unknown",
        };

        const sourceLabel = (value: string | null) => {
            return (value && sourceLabels[value]) || "Unknown";
        };

        const handleClickResetPasswordBtn = (row: any) => {
            setActiveModal("password");
            setSelectedRow(row);
        };

        const passwordButton = (row: any) => {
            return (
                <Button
                    color="secondary"
                    style={{
                        height: "40px",
                        alignSelf: "center",
                        whiteSpace: "nowrap",
                    }}
                    outline
                    onClick={() => handleClickResetPasswordBtn(row)}
                >
                    Reset password
                </Button>
            );
        };

        return [
            {
                Header: "User",
                accessor: "user_id",
                Cell: ({ value }: { value: string }) => cellContent(value),
                // Not using default filter, so as to allow for auto focusing this input
                Filter: ({ column }: any) => {
                    return <DefaultColumnFilter column={column} autoFocus />;
                },
                width: 125,
            },
            {
                Header: "Display Name",
                accessor: "name",
                Cell: ({ value }: { value: string }) => cellContent(value),
                width: 125,
            },
            {
                Header: "Email",
                accessor: "email",
                Cell: ({ value }: { value: string }) => cellContent(value),
                width: 125,
            },
            {
                Header: "Permissions",
                accessor: "permissions",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Source",
                accessor: "login_type",
                Cell: ({ value }: { value: string | null }) => cellContent(sourceLabel(value)),
                width: 100,
            },
            {
                Header: "",
                accessor: "password",
                Filter: ({ column }: any) => {
                    return;
                },
                enableFilter: false,
                maxWidth: "80",
                Cell: ({ row }: { row: any }) =>
                    passwordButton(row),
            },
        ];
    }, []);

    const tableProps = {
        columns,
        data: pageData.users || [],
        showPagination: true,
        onSetSelectedRow,
        rowDoubleClickCallback: editRow,
        isLoading,
    };

    const userModalProps = {
        activeModal,
        getPageData,
        onSetActiveModal,
        onSetSelectedRow,
        selectedRow,
        companyUsersWithUsers: pageData.users || [],
        roles: pageData.roles || [],
        integrations: pageData.integrations || [],
    };

    return (
        <Container fluid>
            {/* Header and New/Edit/Delete buttons */}
            <div className={styles.headerAndButtonsBox}>
                <h1>Manage Users</h1>
                <span className={styles.buttonsBox}>
                    <Button
                        color="primary"
                        onClick={() => setActiveModal("newDimension")}
                    >
                        Add Dimension User
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => setActiveModal("newProcore")}
                    >
                        Add Procore User
                    </Button>
                    <Button
                        color="success"
                        onClick={() => setActiveModal("edit")}
                        disabled={!selectedRow}
                    >
                        Edit
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => setActiveModal("delete")}
                        disabled={!selectedRow}
                    >
                        Delete
                    </Button>
                </span>
            </div>

            <ReactTable {...tableProps} />

            <ManageUsersModal {...userModalProps} />
            <ResetPasswordModal {...userModalProps} />
        </Container>
    );
}
